import React, { useEffect, useState } from "react";
import SharedDropdown from "../../../Form/dropdown/Dropdown";
import { TypeEnum } from "../../../Form/enums/TypeEnum";
import SharedInputText from "../../../Form/inputText/InputText";
import { Button } from "primereact/button";
import { FaPlus } from "react-icons/fa";
import { IForm } from "../../../Form/models/Form.model";
import { Utils } from "../../../../../../utils/Utils";
import "./AgentNode.scss";
import { Handle, Position, useReactFlow, Node } from "@xyflow/react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import SharedForm from "../../../Form/Form";

interface AgentNodeForm {
  input: { [key: string]: string }[];
  output: { [key: string]: string };
}

interface IFormContent {
  key: IForm;
  type: IForm;
  value: IForm;
}

const formOptions = [
  { label: "Text", value: TypeEnum.Input },
  { label: "Select", value: TypeEnum.Dropdown },
  { label: "Boolean", value: TypeEnum.Switch },
];

const initialForm: IFormContent = {
  key: {
    label: "",
    field: "inputNode",
    value: "",
    placeholder: "Field name",
    type: TypeEnum.Input,
  },
  type: {
    label: "",
    field: "outputNode",
    value: "",
    options: formOptions,
    type: TypeEnum.Dropdown,
  },
  value: {
    label: "",
    field: "inputMode",
    value: "",
    options: formOptions,
    type: TypeEnum.Dropdown,
  },
};

export default function AgentNode({ id, data }: any) {
  const [formContent, setFormContent] = useState<IFormContent[]>([]);
  const [nodeMaximized, setNodeMaximized] = useState(false);

  const [formExportData, setFormExportData] = useState<AgentNodeForm>({
    input: [],
    output: {},
  });
  const [outputForm, setOutputForm] = useState<{
    label: string;
    field: string;
    value: any;
    options?: any[];
    type: any;
  }>({
    label: "",
    field: "output",
    value: "",
    options: formOptions,
    type: TypeEnum.Dropdown,
  });

  useEffect(() => {
    if (data.formData) {
      setFormExportData(data.formData);
      const initForm: IFormContent[] = [];
      if (data.formData.input) {
        data.formData.input.forEach((formInputItem: any) => {
          initForm.push({
            key: { ...initialForm.key, value: formInputItem.key },
            type: { ...initialForm.type, value: formInputItem.type },
            value: {
              ...initialForm.value,
              label: formInputItem.key,
              value: formInputItem.value,
            },
          });
        });
      }
      setFormContent(initForm);
    } else {
      setFormContent([initialForm]);
    }
  }, [data]);

  useEffect(() => {
    if (data?.formData?.output) {
      setOutputForm({
        label: "",
        field: "output",
        value: data.formData.output.value,
        options: formOptions,
        type: data.formData.output.type,
      });
    }
  }, [data?.formData?.output]);

  const { getNodes, setNodes } = useReactFlow();

  const handleFormData = (
    formType: "key" | "type" | "value",
    value: any,
    index: number
  ) => {
    const formContentValue = Utils.deepClone(formContent).map(
      (item: any, i: number) => {
        if (i === index) {
          item[formType].value = value;
        }
        return item;
      }
    );
    const inputFormData: { [key: string]: string }[] = [];
    formContentValue.forEach((item: any) => {
      const fieldObj: { [key: string]: string } = {};
      fieldObj["key"] = item.key.value;
      fieldObj["type"] = item.type.value;
      fieldObj["value"] = item.value.value;
      inputFormData.push(fieldObj);
    });
    setFormExportData({ ...formExportData, input: inputFormData });
    setFormContent(formContentValue);
  };

  useEffect(() => {
    const updateNodeData: Node[] = getNodes().map((item) => {
      if (data.id === item.data.id) {
        return { ...item, data: { ...item.data, formData: formExportData } };
      }
      return item;
    });
    setNodes(updateNodeData);
  }, [data.id, formExportData, getNodes, setNodes]);

  return (
    <div className="custom-node input-node">
      <div className="node-controls"></div>
      <div className="node-title">
        <h5>{data.title}</h5>
        <div
          className="expand-icon"
          onClick={() => setNodeMaximized(!nodeMaximized)}
        >
          {nodeMaximized && <IoIosArrowUp />}
          {!nodeMaximized && <IoIosArrowDown />}
        </div>
      </div>
      {nodeMaximized && (
        <div className="node-content">
          {!data.isRunMode && (
            <div className="function">
              <SharedDropdown
                item={{
                  label: "Function",
                  field: "function",
                  type: TypeEnum.Dropdown,
                  value: data.formData.functions[0],
                  options: data.formData.functions.map((func: string) => {
                    return {
                      label: func,
                      value: func,
                    };
                  }),
                }}
                handleFormData={(v: string) => {}}
              />
            </div>
          )}
          <div className="input">
            <div className="node-content-title">
              <label>Input</label>
              {!data.isRunMode && (
                <Button
                  rounded
                  className="secondary"
                  onClick={() =>
                    setFormContent((prev) => prev.concat([initialForm]))
                  }
                >
                  <FaPlus />
                </Button>
              )}
            </div>
            {formContent.map((item, i) => {
              return (
                <div
                  className={`node-form-group ${data.isRunMode ? "" : "edit-mode"}`}
                  key={i}
                >
                  {data.isRunMode ? (
                    <>
                      {item && (
                        <SharedForm
                          form={[
                            {
                              label: item.key.value,
                              field: item.key.value,
                              value: item.value.value,
                              type: item.type.value,
                              options: formOptions,
                            },
                          ]}
                          setForm={() => {}}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <SharedInputText
                        item={item.key}
                        handleFormData={(v: string) =>
                          handleFormData("key", v, i)
                        }
                      />
                      <SharedDropdown
                        item={item.type}
                        handleFormData={(v: string) =>
                          handleFormData("type", v, i)
                        }
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="output">
            <div className="node-content-title">
              <label>Output</label>
            </div>
            <div className="form">
              {data.isRunMode ? (
                <>
                  <SharedForm
                    form={[
                      {
                        ...outputForm,
                        options: formOptions,
                      },
                    ]}
                    setForm={() => {}}
                  />
                </>
              ) : (
                <SharedDropdown
                  item={outputForm}
                  handleFormData={(v: any) => {
                    setOutputForm({ ...outputForm, value: v });
                    setFormExportData({
                      ...formExportData,
                      output: { type: v },
                    });
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {data.isRunMode ? (
        <>
          <Handle type="target" position={Position.Top} />
          <Handle type="source" position={Position.Bottom} />
        </>
      ) : (
        <>
          <Handle type="target" position={Position.Left} />
          <Handle type="source" position={Position.Right} />
        </>
      )}
    </div>
  );
}
